import api from "./client";
import { invalidateVocabularyStats } from "@/hooks/useVocabularyStats";
import type {
  Word, WordLookupResult, WordSuggestion, WordCloudResponse, KnownLevel, StrengthLevel,
} from "@/types/word";

export function lookupWord(
  languageId: number,
  form: string,
  sentence: string,
  dictionaryOnly = false,
) {
  return api.post<{ data: WordLookupResult }>("/words/lookup", {
    language_id: languageId,
    form,
    sentence,
    dictionary_only: dictionaryOnly,
  });
}

/** Asks the AI what a hand-typed word is. Stores nothing. */
export function suggestWord(languageId: number, form: string) {
  return api.post<{ data: WordLookupResult }>("/words/suggest", {
    language_id: languageId,
    form,
  });
}

/**
 * Adds an AI suggestion the learner approved to the lexicon, which maps out its forms.
 * `existing` means the dictionary already had the word, so nothing was written.
 */
export function createWordFromSuggestion(languageId: number, suggestion: WordSuggestion, edited: boolean) {
  return api.post<{ data: Pick<Word, "id" | "lemma" | "part_of_speech">; existing: boolean }>("/words", {
    language_id: languageId,
    suggestion: { ...suggestion, edited },
  });
}

export function fetchWordCloud(languageId: number) {
  return api.get<WordCloudResponse>("/user_word_senses", {
    params: { language_id: languageId },
  });
}

export function fetchWord(languageId: number, id: number) {
  return api.get<{ data: Word }>(`/languages/${languageId}/words/${id}`);
}

export function trackWord(
  languageId: number,
  wordId: number,
  surfaceForm: string,
  contextSentence: string,
  knownLevel: KnownLevel | null = null,
) {
  return api
    .post<{ tracked: boolean; strength: KnownLevel | null }>("/user_word_senses", {
      language_id: languageId,
      word_id: wordId,
      surface_form: surfaceForm,
      context_sentence: contextSentence,
      strength: knownLevel,
    })
    .then((res) => {
      invalidateVocabularyStats(languageId);
      return res;
    });
}

/** Stops tracking a word, discarding its review progress. */
export function untrackWord(languageId: number, wordId: number) {
  return api
    .delete(`/user_word_senses/${wordId}`, { params: { language_id: languageId } })
    .then((res) => {
      invalidateVocabularyStats(languageId);
      return res;
    });
}

/** Sets an already-tracked word's schedule by hand, discarding its review progress. */
export function setWordStrength(languageId: number, wordId: number, level: StrengthLevel) {
  return api
    .patch<{ strength: StrengthLevel }>(`/user_word_senses/${wordId}`, {
      language_id: languageId,
      strength: level,
    })
    .then((res) => {
      invalidateVocabularyStats(languageId);
      return res;
    });
}
